import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PillCard from '../PillCard/PillCard';
import { getReservoir, purchaseGolden } from '../../api/golden';
import { equipSkillTreeNode, unequipSkillTreeNode } from '../../api/skillTree';
import { activateNeuralRewire } from '../../api/psychedelicJourney';
import apiClient from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { isAnonDemoSession } from '../../utils/authStorage';
import { useTokens } from '../../context/TokenContext';
import { playerQueryKeys } from '../../hooks/usePlayerState';
import { getKeystoneDescriptor, getKeystonePillCardProps } from '../../data/keystoneCards';
import { nodeDetailLayoutProps } from './nodeDetailLayout';
import { effectAugment, drugDisplayName } from '../../data/effectDrugMap';
import { lockKeystone } from '../../api/keystones';
import { buildDrugColorMap, normalizeOwnableSlug, handlerColor } from './skillTreeLayout';
import { htnAxisAccent } from '../../utils/htnBlister';
import { normalizeSkillTreeDefinition } from '../SkillTreeMap';
import './SkillTreeNodeToast.css';

// Edward 2026-05-19: no outer container, no X button, no purchase row.
// The toast is JUST the floating PillCard; the purchase action is inline
// in the PillCard's foot (footAction prop) next to the type tag. The
// clicked node itself shows an "active" outer ring (see ProgressMap +
// SkillTreeOwnedCluster) signalling "click again to close" — so the X
// button is redundant.

const FALLBACK_EFFECT = (cardName) => ({
  id: 'unowned',
  name: cardName || 'Card not yet earned',
  type: 'combat',
  // Tooltip-canon grammar: verb-first, one clause, no preamble — the
  // "not yet earned" state is already carried by the effect name + lock visuals.
  description: 'Master this drug in combat to earn its golden card',
});

function resolveDrugSlug(node) {
  if (!node) return null;
  const stm = node._stm || node;
  const payload = stm.payloadData || stm.payload?.data;
  return payload?.drug_slug || payload?.card_slug || stm.id || null;
}

function resolveCardName(node, fallbackName) {
  if (!node) return fallbackName || '';
  const stm = node._stm || node;
  const payload = stm.payloadData || stm.payload?.data;
  return payload?.card_name || stm.label || fallbackName || '';
}

function matchReservoirEntry(reservoir, slug) {
  if (!Array.isArray(reservoir) || !slug) return null;
  // Normalize both sides: lowercase, strip non-alphanumerics. Handles the
  // substrate-uses-underscore_slug vs reservoir-uses-"Generic Name" gap
  // (e.g. node drug_slug "sodium_bicarbonate" must match reservoir entry
  // "Sodium Bicarbonate").
  const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const target = norm(slug);
  return reservoir.find((entry) => norm(entry?.generic_name) === target) || null;
}

const TOAST_GAP = 10;
const VIEWPORT_MARGIN = 8;

// (The ≤768px `useMobileModalMode` hook + centered-modal breakpoint were retired
// 2026-07-10 — the movable float is now the ONE node-detail surface on every
// viewport, sized responsively for phone widths via CSS. See .stn-toast--float.)

export default function SkillTreeNodeToast({ node, anchor, nodeRect, pinned = false, readOnly = false, asFloat: asFloatProp = false, draftEquip = null, onClose, onPointerEnter, onPointerLeave }) {
  // Draft context (HTN blister reward, Edward 2026-07-20): when `draftEquip` is
  // supplied, this is the SAME faithful drug node-toast, but the acquire action
  // (equip/buy) does not hit the skill-tree loadout API — it calls back into the
  // blister's own draft-commit (`draftEquip(drugSlug)`), because the blister grants
  // into the local HTN regimen, not the golden/skill-tree substrate. Everything
  // else (PillCard body, influence rows, glyph, accent, float chrome) is unchanged,
  // so the blister reuses the true toast instead of a hand-rolled clone.
  // ONE node-detail pattern on EVERY viewport (Edward 2026-07-10 FULL RETIREMENT of
  // the centered modal): the movable node-toast float. BOTH the map/tree node CLICK
  // (asFloatProp) AND the equip-hotbar CLICK (readOnly) render as the movable,
  // no-scrim float — drugs AND keystones, desktop AND mobile. The old centered
  // ≤768px modal (stn-toast--tip-modal / --map-modal + scrim) is retired from the
  // live app; it now lives ONLY in the admin Combat-Surfaces gallery. A readOnly /
  // canvas click with no map region to float into still falls back to the anchored
  // in-place toast.
  const wantsFloat = asFloatProp || readOnly;

  // The movable float is scoped to the MAP region (.deck-map-area — the deck-map /
  // skill-tree canvas). We portal INTO that container (position:relative) so
  // position:absolute left/top + the drag clamp bind to it. Falls back to the
  // anchored in-place toast when no map region exists.
  const mapViewportEl = (wantsFloat && typeof document !== 'undefined')
    ? document.querySelector('.deck-map-area')
    : null;
  const mapScoped = !!mapViewportEl;
  // Float only when we actually have a map region to scope + clamp to; otherwise
  // the node click degrades to the anchored pinned toast (its pre-consolidation
  // form). `useModalChrome` drives the shared 344px card layout (the inlined
  // side-panel + header grid).
  const asFloat = wantsFloat && mapScoped;
  const useModalChrome = asFloat;
  // Anon preview: the card toast is informational only — no equip/unequip/
  // switch affordances. The reroll button is the anon loadout lever; manual
  // loadout management is a logged-in feature (Edward 2026-06-12).
  // Detection needs BOTH signals: on the landing the anon run is token-only
  // (drug_lab_anon_token; AuthContext user stays null — verified live), while
  // other anon flows hydrate user.is_anonymous.
  const { user } = useAuth();
  const isAnonPreview = !!user?.is_anonymous || isAnonDemoSession();
  // HTN "Regimen" region cards (region:"htn" on the mapped node) are equippable
  // by anon /play visitors — Edward 2026-07-02 decision #2. The normal anon
  // preview suppresses the Equip action; HTN cards are the deliberate exception
  // so a logged-out visitor can build a regimen loadout from the portal region.
  const isHtnCard = String(node?.region || node?._stm?.region || '').toLowerCase() === 'htn';
  const drugSlug = resolveDrugSlug(node);
  const cardName = resolveCardName(node);

  // Keystones are not in the gold-card reservoir — they're a separate
  // pick-once system. When the clicked node is a keystone, short-circuit
  // the reservoir lookup and render a special keystone PillCard variant
  // with the keystone's accent color + icon (per getKeystonePillCardProps).
  // Detect via any of: node.type==='keystone', _stm.payloadHandler ===
  // 'keystone_marker', or node.id matches a known keystone slug.
  const keystoneDescriptor = (
    getKeystoneDescriptor(node?._stm?.payloadData?.keystone_slug) ||
    getKeystoneDescriptor(node?.id) ||
    getKeystoneDescriptor(drugSlug) ||
    getKeystoneDescriptor(cardName)
  );
  const keystoneProps = keystoneDescriptor
    ? getKeystonePillCardProps(keystoneDescriptor.id)
    : null;
  const queryClient = useQueryClient();
  const { refetchBalance } = useTokens();
  const [purchaseError, setPurchaseError] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const toastRef = useRef(null);
  const [position, setPosition] = useState({ left: -9999, top: -9999, opacity: 0 });
  // True when the card is placed BELOW its node (it flipped because there was no
  // room above). Drives which side the inline error renders on so the error
  // never lands on the node itself — the node is the click-again-to-close
  // affordance, and an error covering it traps the popup open. Default layout is
  // card-above-node, so the error renders ABOVE the card (away from the node);
  // when flipped to card-below-node, the error renders below. (Edward 2026-06-09)
  const [placedBelowNode, setPlacedBelowNode] = useState(false);
  // Side-details panel direction: extend right by default, flip left when the
  // toast sits too close to the right edge for the ~222px panel to fit.
  const [panelFlipLeft, setPanelFlipLeft] = useState(false);

  // Movable-toast drag (desktop node-click float). The grip is the drag handle;
  // it moves the toast within the map region, clamped to the map bounds. Position
  // lives in the same `position` state the layout effect seeds, so a drag simply
  // overrides it (the effect does not re-run mid-drag — its deps don't change).
  const [isDragging, setIsDragging] = useState(false);
  const posRef = useRef(position);
  useEffect(() => { posRef.current = position; }, [position]);
  const dragRef = useRef(null);
  const onDragMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const mapEl = typeof document !== 'undefined' ? document.querySelector('.deck-map-area') : null;
    const t = toastRef.current;
    if (!mapEl || !t) return;
    const m = mapEl.getBoundingClientRect();
    const tr = t.getBoundingClientRect();
    let left = d.startLeft + (e.clientX - d.startX);
    let top = d.startTop + (e.clientY - d.startY);
    // Clamp inside the map: side/bottom 8px, top 18px (grip sits ~11px above card).
    left = Math.max(8, Math.min(left, m.width - tr.width - 8));
    top = Math.max(18, Math.min(top, m.height - tr.height - 8));
    setPosition({ left, top, opacity: 1 });
  }, []);
  const onDragEnd = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragEnd);
  }, [onDragMove]);
  const onGripPointerDown = useCallback((e) => {
    // Never start a drag on an interactive control (footer buttons, lens header
    // handles). The grip is the only handle today so this is belt-and-suspenders,
    // but it keeps the grip durable if the drag surface ever widens.
    if (e.target?.closest?.('button, a, input, [role="button"]')) return;
    e.preventDefault();
    e.stopPropagation();
    const p = posRef.current || {};
    dragRef.current = { startX: e.clientX, startY: e.clientY, startLeft: p.left || 0, startTop: p.top || 0 };
    setIsDragging(true);
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragEnd);
  }, [onDragMove, onDragEnd]);
  useEffect(() => () => {
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragEnd);
  }, [onDragMove, onDragEnd]);

  // Dual-lens (design port, Edward 2026-07-09): the movable float toast's header
  // labels flip the card between the game "effect" view and a "drug" clinical
  // view. State lives here (the toast owns the card lifecycle); PillCard renders
  // the handles + the lens-aware body. The swap cross-fades: mark swapping ->
  // 150ms -> flip the lens + clear. Reset to the effect lens on node change.
  const [lens, setLens] = useState('effect');
  const [lensSwapping, setLensSwapping] = useState(false);
  // Optimistic equip state: node._stm.status is a snapshot frozen when the toast
  // opened and does NOT react to this card's own equip/unequip. After a successful
  // local toggle we overlay 'equipped'/'unequipped' so the buttons + labels flip
  // immediately; reset when the toast reopens on a different node (below).
  const [equipOverride, setEquipOverride] = useState(null);
  const lensRef = useRef('effect');
  const lensTimerRef = useRef(null);
  useEffect(() => { lensRef.current = lens; }, [lens]);
  const selectLens = useCallback((which) => {
    if (lensRef.current === which) return;
    if (lensTimerRef.current) clearTimeout(lensTimerRef.current);
    setLensSwapping(true);
    lensTimerRef.current = setTimeout(() => {
      setLens(which);
      setLensSwapping(false);
      lensTimerRef.current = null;
    }, 150);
  }, []);
  useEffect(() => {
    setLens('effect');
    setLensSwapping(false);
    setEquipOverride(null);
    if (lensTimerRef.current) { clearTimeout(lensTimerRef.current); lensTimerRef.current = null; }
  }, [node?.id]);
  useEffect(() => () => { if (lensTimerRef.current) clearTimeout(lensTimerRef.current); }, []);

  const reservoirQuery = useQuery({
    queryKey: ['golden', 'reservoir'],
    queryFn: getReservoir,
    staleTime: 60_000,
    enabled: !!node,
  });

  const balanceQuery = useQuery({
    queryKey: ['currency', 'balance'],
    queryFn: () => apiClient.get('/api/currency/').then((r) => r.data?.balance || 0),
    staleTime: 30_000,
    enabled: !!node,
  });

  // Skill-tree identity accent for non-keystone cards (Edward 2026-06-12:
  // "respect the color they are an augment of … green and blue where
  // appropriate, yellow the default, just like how the skill tree looks").
  // Same recipe as SkillTreeOwnedCluster: the player-state tree_definition
  // through the shared resolveNodeIdentityColor rule (via buildDrugColorMap),
  // so the toast accent stays in lockstep with the tree/hotbar automatically —
  // metformin augments emerald, adalimumab blue, vancomycin red, warfarin
  // purple, plain card_unlock gold. NOT the full keystone chrome — accent only.
  const playerStateQuery = useQuery({
    queryKey: ['player', 'skill-tree'],
    queryFn: () => apiClient.get('/api/skill-tree/player-state').then((r) => r.data),
    staleTime: 30_000,
    enabled: !!node && !keystoneDescriptor,
  });
  const identityAccent = useMemo(() => {
    if (keystoneDescriptor) return null;
    const goldDefault = handlerColor('card_unlock');
    const td = playerStateQuery.data?.tree_definition;
    if (!td || !drugSlug) return goldDefault;
    const userProgress = playerStateQuery.data?.user_progress
      || { node_states: playerStateQuery.data?.node_states || {} };
    const graph = normalizeSkillTreeDefinition(td, userProgress);
    const colorMap = buildDrugColorMap(graph, playerStateQuery.data?.equipped_keystone || null);
    return colorMap.get(normalizeOwnableSlug(drugSlug)) || goldDefault;
  }, [keystoneDescriptor, playerStateQuery.data, drugSlug]);

  // HTN drug toast accent. Precedence:
  //   1. Keystone-augment lineage WINS. A drug wired under a keystone resolves
  //      (via resolveNodeIdentityColor) to that keystone's color — e.g. propranolol
  //      is an Adalimumab augment, so it stays Adalimumab-blue (#60a5fa), NOT the
  //      beta-blocker green pharmacology axis. Any real tree-identity color
  //      (anything other than the generic gold card_unlock default) is kept.
  //   2. Otherwise the drug's pharmacology-axis accent fills in (indigo RAAS /
  //      amber CCB / teal diuretic) — this is what the authored reference paints
  //      for the plain reservoir starters, which would otherwise fall back to gold.
  // Scoped to the HTN regimen (htnAxisAccent → null elsewhere), so no other card
  // changes. Edward-approved 2026-07-19; refines the 2026-06-12 tree-lockstep rule
  // (keep keystone lineage; give non-augment reservoir cards their axis, not gold).
  const cardAccent = useMemo(() => {
    if (keystoneDescriptor) return null;
    const gold = handlerColor('card_unlock');
    if (identityAccent && identityAccent !== gold) return identityAccent;
    return htnAxisAccent(drugSlug) || identityAccent;
  }, [keystoneDescriptor, identityAccent, drugSlug]);

  const reservoirEntry = useMemo(
    () => matchReservoirEntry(reservoirQuery.data, drugSlug),
    [reservoirQuery.data, drugSlug],
  );

  // Drug-lens clinical copy (design port): the reservoir entry already carries
  // pharmacology fields (mechanism_of_action, indications, …) that pillCardProp
  // otherwise drops. Surface the mechanism of action as the drug-lens body. When
  // absent (sparse cards / keystones) the lens handle stays inert so no blank
  // drug view ever shows — the ~29 sparse reservoir cards are a follow-on
  // backend backfill, not a blocker.
  const drugLensDesc = useMemo(() => {
    if (keystoneDescriptor) return null;
    const moa = reservoirEntry?.mechanism_of_action;
    const text = (typeof moa === 'string' ? moa : '').trim();
    return text.length > 2 ? text : null;
  }, [keystoneDescriptor, reservoirEntry]);

  const pillCardProp = useMemo(() => {
    // Keystone path: build a synthetic card from the keystone descriptor
    // (no reservoir entry exists for keystones — they're picked once at run
    // start, not purchased from the gold-card economy).
    if (keystoneDescriptor) {
      return {
        effect: {
          id: `keystone_${keystoneDescriptor.id}`,
          name: keystoneDescriptor.abilityName,
          type: 'combat',
          // Authored hover-tooltip content (keystoneCards.jsx) so the keystone
          // side panel reads like the regular drug gold equips. Falls back to
          // the pick-screen one-liner for any keystone not yet authored.
          description: keystoneDescriptor.description
            || keystoneDescriptor.infoBody || keystoneDescriptor.infoHeader || '',
          // Two-section keystone tooltip (Edward 2026-06-26): TOP = how the
          // beats work, BOTTOM = the concrete game effects. Fall back to the
          // single description for any keystone not yet split.
          beatsBody: keystoneDescriptor.beatsBody
            || keystoneDescriptor.description || keystoneDescriptor.infoBody || '',
          effectsBody: keystoneDescriptor.effectsBody || '',
          // Per-section mechanic-ledger chips: beatGains/beatCosts ride Section 1
          // (the beat), pros/cons ride Section 2 (the Heal action). Authored on the
          // keystone card; absent for keystones that have not split their chips.
          beatGains: keystoneDescriptor.beatGains || [],
          beatCosts: keystoneDescriptor.beatCosts || [],
          pros: keystoneDescriptor.pros || [],
          cons: keystoneDescriptor.cons || [],
          // Consolidated canonical ledger chips (glyph+word, hover reveals the
          // number+magnitude). Per-section: beatLedger -> Section 1, healLedger ->
          // Section 2. Absent for keystones not yet migrated (they fall back to the
          // legacy beatGains/pros chip rows in PillCard).
          beatLedger: keystoneDescriptor.beatLedger || [],
          healLedger: keystoneDescriptor.healLedger || [],
          // Section model: singleSection collapses the two-column grid to one
          // section (Vancomycin = one effect); custom eyebrows per keystone.
          singleSection: !!keystoneDescriptor.singleSection,
          beatsLabel: keystoneDescriptor.beatsLabel || '',
          effectsLabel: keystoneDescriptor.effectsLabel || '',
        },
        generic_name: keystoneDescriptor.genericName,
        drug_class: keystoneDescriptor.drugClass,
        cost: 0,
      };
    }
    if (reservoirEntry) {
      return {
        effect: reservoirEntry.effect || FALLBACK_EFFECT(cardName),
        generic_name: reservoirEntry.generic_name || cardName,
        drug_class: reservoirEntry.drug_class || '',
        cost: reservoirEntry.cost ?? 0,
        is_starter: !!reservoirEntry.is_starter,
      };
    }
    return {
      effect: FALLBACK_EFFECT(cardName),
      generic_name: cardName,
      drug_class: '',
      cost: 0,
    };
  }, [keystoneDescriptor, reservoirEntry, cardName]);

  const balance = balanceQuery.data || 0;
  const purchaseInfo = useMemo(() => {
    if (!reservoirEntry) return { kind: 'unknown' };
    const isOwned = reservoirEntry.user_status === 'golden' || reservoirEntry.is_chosen_starter;
    if (isOwned) return { kind: 'owned' };
    const cost = reservoirEntry.cost || 0;
    const isPendingAuthoring = reservoirEntry.gate_status === 'pending_authoring';
    const criterion = reservoirEntry.criterion_progress;
    const criterionMet = criterion ? !!criterion.completed_at : true;
    const requiredAchievement = reservoirEntry.required_achievement;
    const legacyMet = !requiredAchievement || reservoirEntry.achievement_earned;
    const isLocked = isPendingAuthoring || (criterion ? !criterionMet : (requiredAchievement && !legacyMet));
    if (isPendingAuthoring) return { kind: 'pending_authoring' };
    if (isLocked) {
      let lockText = 'Locked';
      if (criterion) lockText = `${criterion.current_value}/${criterion.target_value}`;
      return { kind: 'locked', lockText };
    }
    const canBuy = reservoirEntry.can_purchase;
    if (!canBuy) return { kind: 'unavailable', cost };
    if (balance < cost) return { kind: 'cant_afford', cost };
    return { kind: 'buy', cost };
  }, [reservoirEntry, balance]);

  const handlePurchase = useCallback(async () => {
    if (draftEquip) { draftEquip(drugSlug); return; }
    if (purchaseInfo.kind !== 'buy' || purchasing || !reservoirEntry) return;
    setPurchasing(true);
    setPurchaseError(null);
    try {
      await purchaseGolden(reservoirEntry.generic_name);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['golden', 'reservoir'] }),
        queryClient.invalidateQueries({ queryKey: ['currency', 'balance'] }),
        queryClient.invalidateQueries({ queryKey: playerQueryKeys.reservoir }),
        refetchBalance(),
      ]);
    } catch (err) {
      setPurchaseError(err.response?.data?.detail || err.message || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  }, [purchaseInfo, purchasing, reservoirEntry, queryClient, refetchBalance, draftEquip, drugSlug]);

  // Equip/unequip the node's card directly from the card. The canvas click
  // now only OPENS this card (SkillTreeViewport.handleNodeClick), so the
  // loadout mutation lives here as an explicit in-card action. Errors surface
  // inline below — no navigation away.
  const nodeStatus = node?._stm?.status;
  // Override-aware: a local toggle flips these immediately; otherwise they read
  // the frozen node snapshot. equipReady after an unequip is true (we own it),
  // and false after an equip (it's now equipped).
  const isEquipped = equipOverride ? equipOverride === 'equipped' : nodeStatus === 'equipped';
  const equipReady = equipOverride === 'unequipped'
    ? true
    : equipOverride === 'equipped'
      ? false
      : (nodeStatus === 'unlocked'
        || nodeStatus === 'unlockable-now'
        || nodeStatus === 'pursuable');
  const [equipBusy, setEquipBusy] = useState(false);
  const [equipError, setEquipError] = useState(null);
  const handleEquipToggle = useCallback(async () => {
    if (draftEquip) { draftEquip(drugSlug); return; }
    if (equipBusy || !node?.id) return;
    setEquipBusy(true);
    setEquipError(null);
    const willUnequip = isEquipped;
    try {
      if (willUnequip) await unequipSkillTreeNode(node.id);
      else await equipSkillTreeNode(node.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['player', 'skill-tree'] }),
        queryClient.invalidateQueries({ queryKey: ['golden', 'reservoir'] }),
      ]);
      // The frozen node snapshot won't refresh while the toast stays open, so
      // reflect the new state locally — the buttons/labels flip right away.
      setEquipOverride(willUnequip ? 'unequipped' : 'equipped');
    } catch (err) {
      setEquipError(err.response?.data?.detail || err.message || 'Equip failed');
    } finally {
      setEquipBusy(false);
    }
  }, [equipBusy, isEquipped, node, queryClient, draftEquip, drugSlug]);

  // Keystone free-swap (Edward 2026-06): players start with one keystone but
  // may switch between the three freely from the skill tree — augments stay
  // gated (they only FIRE under their matching keystone, and the core augments
  // can only be UNLOCKED while it's equipped), so swapping is safe outside
  // combat. The backend (`POST /api/keystones/lock`) overwrites slot_0,
  // re-resolves synergies, and rejects swaps mid-combat with a 409.
  const equippedKeystoneSlug = String(
    queryClient.getQueryData(['player', 'skill-tree'])?.equipped_keystone || '',
  ).toLowerCase();
  const isThisKeystoneEquipped = !!keystoneDescriptor
    && equippedKeystoneSlug === String(keystoneDescriptor.id || '').toLowerCase();
  const [keystoneSwapBusy, setKeystoneSwapBusy] = useState(false);
  const [keystoneSwapError, setKeystoneSwapError] = useState(null);
  const handleKeystoneSwap = useCallback(async () => {
    if (keystoneSwapBusy || !keystoneDescriptor?.id || isThisKeystoneEquipped) return;
    setKeystoneSwapBusy(true);
    setKeystoneSwapError(null);
    try {
      await lockKeystone(keystoneDescriptor.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['player', 'skill-tree'] }),
        queryClient.invalidateQueries({ queryKey: ['golden', 'reservoir'] }),
        queryClient.invalidateQueries({ queryKey: playerQueryKeys.reservoir }),
      ]);
    } catch (err) {
      setKeystoneSwapError(err.response?.data?.detail || err.message || 'Switch failed');
    } finally {
      setKeystoneSwapBusy(false);
    }
  }, [keystoneSwapBusy, keystoneDescriptor, isThisKeystoneEquipped, queryClient]);

  // Neural Rewire (Psilocybin) — the only consumable with an in-game activation
  // affordance today. Activating it launches the Psychedelic Journey gauntlet
  // (or resumes the active one); a completed trial applies the effect directly.
  const navigate = useNavigate();
  const location = useLocation();
  const isNeuralRewire = pillCardProp?.effect?.id === 'neural_rewire';
  const [journeyBusy, setJourneyBusy] = useState(false);
  const [journeyError, setJourneyError] = useState(null);
  // Slotless activation — Psilocybin is a consumable triggered straight from the
  // node, not an equippable card, so there is no slot to resolve.
  const handleActivateNeuralRewire = useCallback(async () => {
    if (journeyBusy) return;
    setJourneyBusy(true);
    setJourneyError(null);
    try {
      const r = await activateNeuralRewire();
      if (r?.journey_required || r?.journey_in_progress) {
        onClose?.();
        navigate('/learn/deck/psychedelic-journey/boss', {
          state: {
            backgroundLocation: location,
            deckId: 'psychedelic-journey',
            miniBossEncounter: { encounterKind: 'psychedelic_journey_stage' },
          },
        });
        return;
      }
      // A completed trial paid out directly (no gauntlet needed).
      await queryClient.invalidateQueries({ queryKey: ['equipment', 'slots'] });
      onClose?.();
    } catch (err) {
      setJourneyError(err.response?.data?.detail || err.message || 'Activation failed');
    } finally {
      setJourneyBusy(false);
    }
  }, [journeyBusy, navigate, location, onClose, queryClient]);

  // Placement: center the toast horizontally on the node and prefer above.
  // For nodes too close to the top edge (no room above), flip below. Falls
  // back to the click anchor when no node rect was captured (legacy paths).
  //
  // "Top edge" is the skill-tree viewport body's top (not the window's top)
  // so the toast never spills into the chrome above the tree (carousel bar,
  // top status row, table-of-contents pane header, etc). When there is no
  // skill-tree body (e.g. cluster atom in PlayerTray on a non-skill-tree
  // route), fall back to the window margin.
  useLayoutEffect(() => {
    if (asFloat) {
      // Always open dead-CENTER of the map viewport (Edward 2026-07-10): the movable
      // float is seeded at the middle of .deck-map-area regardless of which node was
      // clicked, so every open lands in the same predictable spot instead of skittering
      // around the map next to its node. All coords are map-relative (the toast is
      // portalled into .deck-map-area). After this the user can drag it anywhere via
      // the grip; the drag handler owns `position` from then on and this effect does
      // not re-run (deps unchanged).
      const mapEl = typeof document !== 'undefined' ? document.querySelector('.deck-map-area') : null;
      if (!toastRef.current || !mapEl) { setPosition({ left: 16, top: 18, opacity: 1 }); return; }
      const t = toastRef.current.getBoundingClientRect();
      const m = mapEl.getBoundingClientRect();
      const w = t.width;
      const h = t.height;
      let left = (m.width - w) / 2;                          // horizontally centered in the map
      let top = (m.height - h) / 2;                          // vertically centered in the map
      left = Math.max(8, Math.min(left, m.width - w - 8));
      top = Math.max(18, Math.min(top, m.height - h - 8));
      setPosition({ left, top, opacity: 1 });
      setPlacedBelowNode(false);
      setPanelFlipLeft(false);
      return;
    }
    if (!toastRef.current) return;
    const rect = toastRef.current.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const treeBody = document.querySelector('.skill-tree-viewport__body');
    const bodyRect = treeBody ? treeBody.getBoundingClientRect() : null;
    const topBound = bodyRect ? bodyRect.top + VIEWPORT_MARGIN : VIEWPORT_MARGIN;
    const bottomBound = bodyRect ? bodyRect.bottom - VIEWPORT_MARGIN : vh - VIEWPORT_MARGIN;

    // Use the node's bounding rect when we have it (centered horizontally on
    // node center, vertically anchored on top/bottom edge of node). Fall back
    // to the click anchor as a point with zero extent.
    const cx = nodeRect ? (nodeRect.left + nodeRect.width / 2) : (anchor?.x ?? vw / 2);
    const nodeTop = nodeRect ? nodeRect.top : (anchor?.y ?? vh / 2);
    const nodeBottom = nodeRect ? nodeRect.bottom : (anchor?.y ?? vh / 2);

    let left = cx - w / 2;
    let top = nodeTop - h - TOAST_GAP;
    let belowNode = false;

    // No room above the viewport body → flip below the node.
    if (top < topBound) {
      top = nodeBottom + TOAST_GAP;
      belowNode = true;
    }

    // Clamp inside the window so the toast never clips off-screen. Vertical
    // clamp uses the body bounds when known, otherwise the window.
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - w - VIEWPORT_MARGIN));
    top = Math.max(topBound, Math.min(top, bottomBound - h));

    // Decide which way the hover detail panel extends: prefer right, flip to the
    // left when the right gutter can't fit it and the left side has more room.
    const SIDE_PANEL_W = 222; // 210 panel + 12 gap
    const rightSpace = vw - (left + w);
    const leftSpace = left;
    setPanelFlipLeft(rightSpace < SIDE_PANEL_W && leftSpace > rightSpace);

    setPosition({ left, top, opacity: 1 });
    setPlacedBelowNode(belowNode);
  }, [anchor, nodeRect, node, asFloat]);

  // Esc closes the centered/mobile modal only. The movable float is
  // CLOSE-VIA-X-ONLY (Edward 2026-07-10 consolidation): the sole exit is the
  // X foot button, so the float never vanishes from a stray Escape while the
  // user is reading/dragging it. (The prior pattern kept ambient dismiss paths
  // that felt wonky on the no-scrim float.)
  useEffect(() => {
    if (asFloat) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, asFloat]);

  // Click-outside closes the centered/mobile modal only, NOT the movable float
  // (X-only close, same consolidation as above — a stray map click must never
  // dismiss the float). Uses the `click` event (not `mousedown`) so it fires
  // AFTER any node's React onClick (which calls openNodeToast with its toggle
  // logic) — without this ordering, mousedown would close, then click would
  // re-open, causing the disappear-then-reappear bug Edward saw on the prior
  // iteration. Bail if the click landed inside the toast itself, the skill-tree
  // viewport body (any tree node), or the owned-cluster SVG (any cluster atom) —
  // those paths are handled by openNodeToast's toggle.
  useEffect(() => {
    if (asFloat) return;
    const handler = (e) => {
      if (!toastRef.current) return;
      if (toastRef.current.contains(e.target)) return;
      if (e.target.closest && (
        e.target.closest('.skill-tree-viewport__body') ||
        e.target.closest('svg.stoc-overlay')
      )) return;
      onClose();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [onClose, asFloat]);

  // Compose the type-tag label + the inline foot action based on purchase
  // state. Type tag carries the status text; foot action carries the
  // actionable affordance (only present when something is actionable).
  const isActuallyOwned = purchaseInfo.kind === 'owned';
  const isUnowned = !isActuallyOwned;

  let typeLabel = 'Card';
  let typeMode = null;
  let footAction = null;

  switch (purchaseInfo.kind) {
    case 'owned':
      typeLabel = 'Owned';
      typeMode = 'gold';
      break;
    case 'pending_authoring':
      typeLabel = 'Soon';
      break;
    case 'locked':
      typeLabel = `Locked ${purchaseInfo.lockText}`;
      break;
    case 'unavailable':
      typeLabel = 'Unavailable';
      break;
    case 'cant_afford':
      typeLabel = 'Available';
      footAction = (
        <button type="button" className="stn-foot-btn stn-foot-btn--cant-afford" disabled>
          ${purchaseInfo.cost}
        </button>
      );
      break;
    case 'buy':
      typeLabel = 'Available';
      footAction = (
        <button
          type="button"
          className="stn-foot-btn stn-foot-btn--buy"
          onClick={handlePurchase}
          disabled={purchasing}
        >
          {purchasing ? '…' : (purchaseInfo.cost > 0 ? `$${purchaseInfo.cost}` : 'Free')}
        </button>
      );
      break;
    case 'unknown':
    default:
      typeLabel = isUnowned ? 'Locked' : 'Owned';
      typeMode = isUnowned ? null : 'gold';
      break;
  }

  // Equip/unequip takes precedence over the purchase affordance for nodes on
  // the player's tree (owned-equippable, or already equipped). Keystones are
  // gated separately and have footAction nulled in finalPillProps below.
  // Neural Rewire (Psilocybin) is excluded — it's a non-equippable consumable
  // handled by the Activate block below.
  if (!keystoneDescriptor && !isNeuralRewire && (!isAnonPreview || isHtnCard) && (isEquipped || equipReady)) {
    if (isEquipped) {
      typeLabel = 'Equipped';
      typeMode = 'gold';
    }
    footAction = (
      <button
        type="button"
        className="stn-foot-btn stn-foot-btn--buy"
        onClick={handleEquipToggle}
        disabled={equipBusy}
      >
        {equipBusy ? '…' : (isEquipped ? 'Unequip' : (draftEquip ? 'Draft' : 'Equip'))}
      </button>
    );
  }

  // Psilocybin / Neural Rewire — NOT a traditional equippable card. Once owned
  // it's activated directly from the node (no equip step, no slot): the single
  // Activate button launches the Psychedelic Journey gauntlet (or resumes /
  // pays out a completed trial). The only consumable with an in-game
  // activation entry point today.
  if (!keystoneDescriptor && isNeuralRewire && (isEquipped || equipReady || isActuallyOwned)) {
    footAction = (
      <button
        type="button"
        className="stn-foot-btn stn-foot-btn--buy"
        onClick={handleActivateNeuralRewire}
        disabled={journeyBusy}
      >
        {journeyBusy ? '…' : 'Activate'}
      </button>
    );
  }

  // Keystone overrides — when the node is a keystone, swap to the
  // canonical keystone pill props (accent color, icon, "Keystone" tag)
  // and ignore the gold-card purchase plumbing. Hover-to-flip disabled
  // per Edward 2026-05-24: the face (icon + ability + drug + class +
  // KEYSTONE tag) already conveys everything; no pros/cons/mechanics
  // panel to flip to.
  // Keystone foot action: non-equipped keystones show a "Switch" button that
  // re-locks slot_0 to this keystone. The equipped one shows NOTHING — a
  // keystone is always equipped, so an "Equipped" state tag is redundant
  // (Edward 2026-06-12; replaced the static Equipped span). Anon preview gets
  // no Switch either — reroll is the anon loadout lever.
  const keystoneFootAction = (!keystoneProps || isThisKeystoneEquipped || isAnonPreview)
    ? null
    : (
      <button
        type="button"
        className="stn-foot-btn stn-foot-btn--buy"
        onClick={handleKeystoneSwap}
        disabled={keystoneSwapBusy}
      >
        {keystoneSwapBusy ? '…' : 'Switch'}
      </button>
    );

  // Unlock requirement for an unowned gold card — same shape PlayerShop feeds
  // PillCard, so the tree toast shows the canonical lock-glyph progress row
  // (tooltip canon 2026-06-11; previously only the shop surfaced it).
  const reqCriterion = reservoirEntry?.criterion_progress;
  const requirementMeta = (!keystoneProps && reqCriterion)
    ? {
        name: reservoirEntry?.achievement?.unlock_copy || reqCriterion.criterion_key,
        progress: reqCriterion.current_value,
        threshold: reqCriterion.target_value,
        earned: !!reqCriterion.completed_at,
      }
    : null;

  // Keystone-synergy "bonus" for a gold card — the same augment the combat bar's
  // EffectChip surfaces as a "{keystone} synergy" section. Shown in the side panel
  // so the tree explains how the card behaves in combat once its keystone is on.
  const goldAugment = !keystoneProps ? effectAugment(pillCardProp?.effect) : null;
  const goldKeystoneBonus = (goldAugment && Array.isArray(goldAugment.lines) && goldAugment.lines.length)
    ? {
        keystone: drugDisplayName(goldAugment.keystone_slug),
        keystoneSlug: String(goldAugment.keystone_slug || '').toLowerCase(),
        color: getKeystoneDescriptor(goldAugment.keystone_slug)?.accent || '#fbbf24',
        lines: goldAugment.lines,
        active: String(goldAugment.keystone_slug || '').toLowerCase() === equippedKeystoneSlug,
      }
    : null;

  // ── Reconciled float footer (design port): 4 thin math-glyph action buttons
  // built from the toast's existing handlers. ONLY the desktop node-click float
  // (asFloat), non-keystone, non-consumable path uses it; the mobile centered
  // modal + hotbar tooltip keep their current chrome. Reuses handleEquipToggle /
  // handlePurchase so no mutation path changes — only the button that fires it. ──
  const useReconciledFloat = asFloat && !keystoneProps && !isNeuralRewire;
  const canAct = !isAnonPreview || isHtnCard;
  const footGlyph = (paths) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
  // The + button is the primary action: equip when equippable, buy when an
  // unowned card is purchasable. Disabled once equipped (unequip via the − btn).
  const canEquipPrimary = canAct && !equipBusy && !purchasing && !isEquipped
    && (equipReady || purchaseInfo.kind === 'buy');
  const onPrimaryFootAction = () => {
    if (equipReady) handleEquipToggle();
    else if (purchaseInfo.kind === 'buy') handlePurchase();
  };
  // Synergy button: grayed once this card's synergy is live (card equipped AND
  // its keystone equipped). Otherwise it scrolls the synergy block into view —
  // a harmless, non-mutating "jump to synergy" for now (full keystone-jump is a
  // deferred UX call).
  const synergyLocked = !!(isEquipped && goldKeystoneBonus?.active);
  const onSynergyView = () => {
    const el = toastRef.current?.querySelector('.pill-card__side-bonus');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };
  const floatFootButtons = (
    <>
      <button type="button" className="stn-foot-btn stn-foot-btn--close" data-action="close"
        aria-label="Close" onClick={onClose}>
        {footGlyph(['M6 6l12 12', 'M18 6L6 18'])}
      </button>
      <button type="button" className="stn-foot-btn stn-foot-btn--unequip" data-action="unequip"
        aria-label="Unequip" onClick={handleEquipToggle}
        disabled={!canAct || !isEquipped || equipBusy}>
        {footGlyph(['M5 12h14'])}
      </button>
      <button type="button" className="stn-foot-btn stn-foot-btn--buy" data-action="equip"
        aria-label={draftEquip ? 'Draft' : (purchaseInfo.kind === 'buy' ? 'Buy' : 'Equip')} onClick={onPrimaryFootAction}
        disabled={!canEquipPrimary}>
        {footGlyph(['M12 5v14', 'M5 12h14'])}
      </button>
      {goldKeystoneBonus && (
        <button type="button" className="stn-foot-btn stn-foot-btn--synergy" data-action="synergy"
          aria-label="View synergy" data-keystone-active={String(synergyLocked)}
          onClick={onSynergyView} disabled={synergyLocked}>
          {footGlyph(['M5 15l7-7 7 7'])}
        </button>
      )}
    </>
  );

  // Keystone float foot (design port): keystones are a pick-once SLOT — you switch
  // which one is active (lockKeystone overwrites slot_0), there is no "unequip to
  // nothing". So the movable keystone toast gets close + Switch only (no unequip,
  // no synergy — a keystone IS the synergy). The + glyph switches to this keystone
  // and greys out once it is the equipped one. Anon preview has no loadout lever.
  const keystoneFloatFootButtons = (
    <>
      <button type="button" className="stn-foot-btn stn-foot-btn--close" data-action="close"
        aria-label="Close" onClick={onClose}>
        {footGlyph(['M6 6l12 12', 'M18 6L6 18'])}
      </button>
      <button type="button" className="stn-foot-btn stn-foot-btn--buy" data-action="switch"
        aria-label={isThisKeystoneEquipped ? 'Equipped' : 'Switch to this keystone'}
        onClick={handleKeystoneSwap}
        disabled={keystoneSwapBusy || isThisKeystoneEquipped || isAnonPreview}>
        {footGlyph(['M12 5v14', 'M5 12h14'])}
      </button>
    </>
  );

  // Read-only surfaces (the equip bar) get a pure tooltip — every mutation
  // affordance is suppressed (Edward 2026-06-25). The "what it does" side panel,
  // accent, synergy bonus and requirement row all stay; only the action buttons
  // (Buy / Equip / Unequip / Activate / Switch) drop out.
  const effectiveFootAction = readOnly ? null : footAction;
  const effectiveKeystoneFootAction = readOnly ? null : keystoneFootAction;

  // Neural Rewire (Psilocybin) float foot (design port, Edward 2026-07-11): Psilocybin
  // now rides the SAME canonical float body as the gold drug nodes (it used to drop to
  // the old compact card, which is why the imposed toast style skipped it). It is a
  // slotless consumable, so instead of the equip/unequip/synergy glyph row it reuses its
  // own already state-aware action (Activate when owned, Buy/Locked otherwise ==
  // effectiveFootAction) paired with the standard close button. The reused button carries
  // stn-foot-btn--buy, so the float's --buy/--close CSS styles it as the wide primary action.
  const neuralRewireFloatFootButtons = (
    <>
      <button type="button" className="stn-foot-btn stn-foot-btn--close" data-action="close"
        aria-label="Close" onClick={onClose}>
        {footGlyph(['M6 6l12 12', 'M18 6L6 18'])}
      </button>
      {effectiveFootAction}
    </>
  );

  // Read-only (tooltip) surfaces show the drug's BARE inline glyph via PillCard's
  // own drugNodeGlyph fallback — no ringed node atom, no top-right badge. (Edward
  // 2026-07-12: "remove the circle, just the svg inline with the title header".
  // The float/toast keeps its own icon-ring; only the tooltip changed.)

  // Structural layout flags (which internal PillCard path renders: compact face vs
  // .ks-split float vs centered tooltip) come from the shared nodeDetailLayout source
  // of truth so the live component and the admin Combat-Surfaces gallery can NEVER
  // disagree (Edward 2026-07-10 — the gallery used to hand-mirror these and drifted).
  // Each branch then adds its own CONTEXTUAL props (card, footButtons/iconOverride JSX,
  // accent, keystoneBonus, lens state) which are not derivable from the surface kind.
  const nodeDetailLayout = nodeDetailLayoutProps({
    isKeystone: !!keystoneProps,
    asFloat,
    readOnly,
  });

  const finalPillProps = keystoneProps
    ? {
        card: pillCardProp,
        // Keystone float renders the two-section .ks-split body (sideDetailsLayout);
        // mobile / no-map falls back to the centered keystone-grid tooltip (tooltipOnly).
        // Authored description + pros/cons live on the keystone descriptor.
        ...nodeDetailLayout,
        sideFlip: panelFlipLeft,
        ...keystoneProps,
        footAction: effectiveKeystoneFootAction,
        footButtons: asFloat ? keystoneFloatFootButtons : null,
        // Tooltip header = PillCard's own inline bare glyph fallback; the float uses
        // the split headrow, so keep the keystone's icon there.
        iconOverride: (readOnly && !asFloat) ? undefined : keystoneProps.iconOverride,
      }
    : {
        card: pillCardProp,
        // Reconciled float layout (sideDetailsLayout) opts in only on the desktop node/
        // hotbar click; every other surface renders the classic compact card. hideType:
        // the node's own visual state already conveys owned/equipped/locked.
        ...nodeDetailLayout,
        sideFlip: panelFlipLeft,
        // Accent (stripe / icon ring / drug name / panel trim): keystone-augment
        // lineage wins (e.g. propranolol = Adalimumab-blue); non-augment HTN
        // reservoir cards get their pharmacology-axis accent per the authored
        // reference; every other card keeps the skill-tree identity accent.
        accentColor: cardAccent,
        keystoneBonus: goldKeystoneBonus,
        requirement: requirementMeta,
        // Normal gold cards get the equip/unequip/synergy glyph row; Psilocybin (the
        // slotless consumable, excluded from useReconciledFloat) gets close + Activate.
        footButtons: useReconciledFloat
          ? floatFootButtons
          : (asFloat && isNeuralRewire ? neuralRewireFloatFootButtons : null),
        lens,
        lensEnabled: useReconciledFloat && !!drugLensDesc,
        lensSwapping,
        onLensSelect: selectLens,
        drugLensDesc,
        typeLabel,
        typeMode,
        footAction: effectiveFootAction,
        // Tooltip header = PillCard's own inline bare glyph fallback (drugNodeGlyph);
        // the float renders its icon-ring separately, so no iconOverride here.
      };

  // For keystone toasts, set --pill-accent on the wrapper BEFORE PillCard
  // mounts so the keystone-variant box-shadow (which uses var(--pill-accent))
  // paints in the keystone color from the very first frame. Without this,
  // PillCard's class .pill-card--combat sets a default red --pill-accent,
  // then the accentColor inline-style override fires, and the box-shadow's
  // 250ms transition animates from red → keystone color (visible flicker).
  // The float (and the anchored fallback) is positioned via `position` (map-relative
  // left/top); the keystone accent custom property rides along in the same style.
  const wrapperStyle = keystoneDescriptor
    ? { ...position, '--pill-accent': keystoneDescriptor.accent }
    : position;

  const toastContent = (
    <>
      {/* No scrim: the float is a movable, no-scrim overlay so the map stays live
          behind it (the centered modal + its scrim were retired 2026-07-10). The
          stn-overlay-layer class keeps us in styles/mobile.css's overlay exemption
          (its generic ≤768px `[class*="modal"]:not([class*="overlay"])` clamp would
          otherwise shrink the layer on a phone). */}
      <div
        ref={toastRef}
        className={`stn-toast${useModalChrome ? ' stn-toast--modal stn-overlay-layer' : ''}${asFloat ? ' stn-toast--float' : ''}${keystoneProps ? ' stn-toast--keystone' : ''}${pinned ? ' stn-toast--pinned' : ''}${isDragging ? ' is-dragging' : ''}`}
        style={wrapperStyle}
        role="dialog"
        aria-label={`Skill tree node: ${cardName || node.id}`}
        onMouseEnter={useModalChrome ? undefined : onPointerEnter}
        onMouseLeave={useModalChrome ? undefined : onPointerLeave}
      >
        {asFloat && (
          <div className="stn-drag-grip" onPointerDown={onGripPointerDown} aria-hidden="true">
            <i /><i /><i /><i /><i /><i />
          </div>
        )}
        <PillCard {...finalPillProps} />
        {(purchaseError || equipError || journeyError) && !keystoneProps && (
          <div
            className={`stn-toast-error${placedBelowNode ? '' : ' stn-toast-error--above'}`}
            role="alert"
          >
            {purchaseError || equipError || journeyError}
          </div>
        )}
        {keystoneSwapError && keystoneProps && (
          <div
            className={`stn-toast-error${placedBelowNode ? '' : ' stn-toast-error--above'}`}
            role="alert"
          >
            {keystoneSwapError}
          </div>
        )}
      </div>
    </>
  );

  // The movable float portals INTO the map region (.deck-map-area) so its
  // position:absolute left/top + drag clamp bind to the map box; when there is no
  // map region the toast degrades to the anchored in-place render.
  if (mapScoped) return createPortal(toastContent, mapViewportEl);
  return toastContent;
}
